import { effect, inject, Injectable, signal } from '@angular/core';
import { API_ENDPOINTS } from '@core/constants/api-endpoints';
import { ChatbotRegisterData, ChatbotRegisterResponse } from '@core/interface/chatbot-interface';
import { Api } from '@core/service/api/api';
import { Session } from '@core/service/session/session';
import { environment } from 'environments/environment';
import { Observable, retry, tap, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private api = inject(Api);
  private session = inject(Session);

  private readonly SCRIPT_ID = 'aalya-chatbot-widget-script';
  private readonly MAX_RETRIES = 10;

  private retryCount = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;

  private _isLoaded = signal<boolean>(false);
  private _isLoading = signal<boolean>(false);
  private _botData = signal<ChatbotRegisterData | null>(null);

  readonly isLoaded = this._isLoaded.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly botData = this._botData.asReadonly();

  constructor() {
    // Automatically manage widget lifecycle according to session state
    effect(() => {
      const isLoggedIn = this.session.isLoggedInSignal();
      if (isLoggedIn && !this._isLoaded() && !this._isLoading()) {
        this.init();
      } else if (!isLoggedIn && (this._isLoaded() || this.isScriptPresent())) {
        this.clear();
      }
    });
  }

  /**
   * Register with the chatbot API with maximum 10 retries
   */
  register(): Observable<ChatbotRegisterResponse> {
    return this.api.post<ChatbotRegisterResponse>(API_ENDPOINTS.CHATBOT.REGISTER, {}).pipe(
      retry({
        count: this.MAX_RETRIES,
        delay: (error, count) => {
          console.warn(`[ChatbotService] Registration attempt ${count}/${this.MAX_RETRIES} failed, retrying...`, error);
          return timer(Math.min(count * 1000, 5000));
        }
      }),
      tap({
        next: (response) => {
          if (response?.data) {
            this._botData.set(response.data);
          }
        },
        error: (err) => {
          console.error(`[ChatbotService] Registration failed after ${this.MAX_RETRIES} attempts:`, err);
        }
      })
    );
  }

  /**
   * Initialize and load chatbot widget for logged-in user
   */
  init(): void {
    if (!this.session.isLoggedIn()) {
      return;
    }

    if (this._isLoaded() || this._isLoading()) {
      return;
    }

    if (this.retryCount >= this.MAX_RETRIES) {
      console.warn(`[ChatbotService] Max retry limit (${this.MAX_RETRIES}) reached. Halting initialization.`);
      return;
    }

    this._isLoading.set(true);

    this.register().subscribe({
      next: (response) => {
        this._isLoading.set(false);
        if (response?.data) {
          const { bot_id, refresh_token, session_token, api_key } = response.data;
          const token = session_token || refresh_token || api_key || '';
          this.loadWidget(bot_id, token);
        }
      },
      error: (err) => {
        this._isLoading.set(false);
        console.error(`[ChatbotService] Failed to initialize chatbot after ${this.MAX_RETRIES} retries:`, err);
      }
    });
  }

  /**
   * Dynamically inject the widget script into the DOM with retry limit (10 times max)
   */
  loadWidget(botId: string, sessionToken: string): void {
    if (typeof document === 'undefined') return;

    // Remove existing script if any
    this.removeScript();

    const script = document.createElement('script');
    script.id = this.SCRIPT_ID;
    script.src = this.getWidgetUrl();
    script.async = true;
    script.setAttribute('data-bot-id', botId);
    script.setAttribute('data-session-token', sessionToken);

    script.onload = () => {
      console.log('[ChatbotService] Widget script loaded successfully');
      this._isLoaded.set(true);
      this.retryCount = 0; // Reset retry counter on success
    };

    script.onerror = (err) => {
      console.error(`[ChatbotService] Failed to load widget script (attempt ${this.retryCount + 1}/${this.MAX_RETRIES}):`, err);
      this._isLoaded.set(false);

      if (this.retryCount < this.MAX_RETRIES) {
        this.retryCount++;
        const backoffTime = Math.min(this.retryCount * 1000, 5000);
        this.retryTimeout = setTimeout(() => {
          if (this.session.isLoggedIn() && !this._isLoaded()) {
            this.loadWidget(botId, sessionToken);
          }
        }, backoffTime);
      } else {
        console.error(`[ChatbotService] Max retries (${this.MAX_RETRIES}) exceeded while loading widget script.`);
      }
    };

    document.body.appendChild(script);
  }

  /**
   * Determine the widget script URL
   */
  private getWidgetUrl(): string {
    if ((environment as any).chatbotWidgetUrl) {
      return (environment as any).chatbotWidgetUrl;
    }
    return '/widget.js?v=6';
  }

  /**
   * Check if script is currently attached in DOM
   */
  private isScriptPresent(): boolean {
    if (typeof document === 'undefined') return false;
    return !!document.getElementById(this.SCRIPT_ID);
  }

  /**
   * Remove the script tag
   */
  private removeScript(): void {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById(this.SCRIPT_ID);
    if (existing) {
      existing.remove();
    }
  }

  /**
   * Clean up widget elements from DOM and reset service state
   */
  clear(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    this.retryCount = 0;
    this.removeScript();

    if (typeof document !== 'undefined') {
      const win = window as any;
      if (typeof win.AalyaChatbot?.destroy === 'function') {
        win.AalyaChatbot.destroy();
      } else if (typeof win.ChatbotWidget?.destroy === 'function') {
        win.ChatbotWidget.destroy();
      }

      const widgetNodes = document.querySelectorAll(
        '#chatbot-widget-container, #chatbot-widget-iframe, .chatbot-widget-root, [id^="chatbot-widget"], iframe[src*="chatbot"], iframe[src*="embed/frame"]'
      );
      widgetNodes.forEach(node => node.remove());
    }

    this._isLoaded.set(false);
    this._isLoading.set(false);
    this._botData.set(null);
  }
}
